import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

const sessionId = (() => {
  let id = localStorage.getItem("mg_chat_session");
  if (!id) {
    id = `web-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    localStorage.setItem("mg_chat_session", id);
  }
  return id;
})();

const GREETING = {
  role: "assistant",
  content: "Hey! 👋 I'm the Mask Giver helper. Ask me how to donate, volunteer, or where we serve.",
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data: /, "").trim();
          if (!line) continue;
          let data;
          try { data = JSON.parse(line); } catch { continue; }
          if (data.t) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.t };
              return copy;
            });
          } else if (data.error) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: data.error };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I couldn't connect. Please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label="Chat with us"
        className="fixed bottom-6 right-6 z-[150] w-15 h-15 rounded-full bg-green text-black flex items-center justify-center box-glow hover:scale-105 transition-transform"
        style={{ width: 60, height: 60 }}
      >
        {open ? <X size={26} /> : <MessageCircle size={26} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[150] w-[92vw] max-w-sm h-[70vh] max-h-[540px] bg-[#0a0a0a] border border-[#1a1a1a] rounded-2xl flex flex-col overflow-hidden shadow-2xl"
          >
            <div className="flex items-center gap-3 px-5 py-4 border-b border-[#1a1a1a] bg-black">
              <span className="font-display text-xl leading-none"><span className="text-white">M</span><span className="text-green">G</span></span>
              <div>
                <p className="font-sub text-white text-sm font-semibold leading-none">Mask Giver Helper</p>
                <p className="text-green text-xs mt-1 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green inline-block" /> Online</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
                  <div
                    className={`max-w-[80%] px-4 py-2.5 rounded-2xl font-sub text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-green text-black rounded-br-sm"
                        : "bg-[#141414] text-zinc-200 rounded-bl-sm border border-[#1f1f1f]"
                    }`}
                  >
                    {m.content || <Loader2 className="animate-spin" size={15} />}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-3 border-t border-[#1a1a1a] flex items-center gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about donating, volunteering…"
                className="flex-1 bg-[#050505] border border-[#1a1a1a] focus:border-green rounded-full px-4 py-2.5 text-white font-sub text-sm outline-none transition-colors"
              />
              <button
                data-testid="chat-send"
                onClick={send}
                disabled={busy}
                className="w-10 h-10 rounded-full bg-green text-black flex items-center justify-center shrink-0 disabled:opacity-60"
              >
                {busy ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
